import { Canvas } from '@react-three/fiber';
import type { ice } from '@halvard/rules';
import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { moveCursor, screenOrder } from '../keyboard.js';
import { cameraPosition } from '../Scene.js';
import type { CameraView, Square } from '../types.js';
import type { IceHints, SinkReason } from './hints.js';
import { ICE_LOOK, iceSquareLabel } from './look.js';
import { IceScene, type IcePlayback } from './IceScene.js';

/** The board's words, so the page can show them in its own language. English by default. */
export interface IceBoardText {
  /** Accessible label of a square, e.g. "e4, ice 2, Brand Huscarl, centre runestone". */
  squareLabel: (state: ice.IceState, r: number, c: number) => string;
  /** What the hints add to a square's label, e.g. ", walk here". */
  hintLabel: (hints: IceHints, r: number, c: number) => string;
  /** The tag over a see-through warrior that would go under, after "✕ ". */
  sinkBadge: (reason: SinkReason) => string;
}

const SINK_BADGES: Readonly<Record<SinkReason, string>> = {
  off: 'off the ice',
  hole: 'into a hole',
  fall: '2-level fall',
  crush: 'crushed',
};

export const ENGLISH_BOARD_TEXT: IceBoardText = {
  squareLabel: iceSquareLabel,
  hintLabel: (hints, r, c) => hintLabel(hints, r, c),
  sinkBadge: (reason) => SINK_BADGES[reason],
};

/** Camera presets: behind Brand, behind Rime, or from above. */
export type IceCameraView = 'brand' | 'rime' | 'top';

export interface IceBoardProps {
  state: ice.IceState;
  hints: IceHints;
  selected?: Square | null;
  /** Keyboard cursor, shown in 3D while the board has keyboard focus. */
  cursor: Square;
  lastMove?: readonly Square[];
  playback?: IcePlayback | null;
  view: IceCameraView;
  /** The side the local player sits on; sets the top view and the arrow keys. */
  facing: ice.Side;
  reducedMotion?: boolean;
  /** Accessible name of the board grid. */
  label?: string;
  text?: IceBoardText;
  onSquareActivate?: (square: Square) => void;
  /** The square under the pointer or the keyboard cursor, for route previews. */
  onSquareFocus?: (square: Square | null) => void;
  onCursorChange?: (square: Square) => void;
  onCancel?: () => void;
  className?: string;
  style?: CSSProperties;
}

const CAMERA: Readonly<Record<IceCameraView, CameraView>> = {
  brand: 'ivory',
  rime: 'basalt',
  top: 'top',
};

/** Arrow keys and screen order reuse v0.1's helpers: Brand sits where Ivory did. */
function team(side: ice.Side): 'I' | 'B' {
  return side === 'brand' ? 'I' : 'B';
}

const hiddenGrid: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const hiddenCell: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: 1,
  height: 1,
  overflow: 'hidden',
};

/**
 * The lake in 3D plus an accessible grid. It renders only what it is given and reports clicks,
 * hovers and key presses through callbacks.
 */
export function IceBoard(props: IceBoardProps) {
  const [focused, setFocused] = useState(false);
  const facing = team(props.facing);
  const text = props.text ?? ENGLISH_BOARD_TEXT;
  const initial = cameraPosition(CAMERA[props.view], facing);
  const activate = (square: Square) => {
    props.onCursorChange?.(square);
    props.onSquareActivate?.(square);
  };

  return (
    <div
      className={props.className}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        borderRadius: 12,
        ...props.style,
      }}
      onPointerLeave={() => props.onSquareFocus?.(null)}
    >
      <Canvas
        shadows
        flat
        dpr={[1, 2]}
        camera={{ fov: 40, position: [initial.x, initial.y, initial.z], near: 0.1, far: 100 }}
        aria-hidden="true"
        onPointerMissed={() => props.onSquareFocus?.(null)}
      >
        <color attach="background" args={[ICE_LOOK.background]} />
        <IceScene
          state={props.state}
          hints={props.hints}
          selected={props.selected ?? null}
          cursor={focused ? props.cursor : null}
          lastMove={props.lastMove ?? []}
          playback={props.playback ?? null}
          view={CAMERA[props.view]}
          facing={facing}
          reducedMotion={props.reducedMotion ?? false}
          sinkBadge={text.sinkBadge}
          onSquareClick={activate}
          onSquareHover={(square) => props.onSquareFocus?.(square)}
        />
      </Canvas>
      {focused && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            border: '3px solid #F1EDE2',
            borderRadius: 12,
            pointerEvents: 'none',
          }}
        />
      )}
      <IceGrid
        state={props.state}
        hints={props.hints}
        cursor={props.cursor}
        selected={props.selected ?? null}
        facing={facing}
        label={props.label ?? 'Lake'}
        text={text}
        onCursorChange={(square) => {
          props.onCursorChange?.(square);
          props.onSquareFocus?.(square);
        }}
        onActivate={activate}
        onCancel={() => props.onCancel?.()}
        onFocusChange={setFocused}
      />
    </div>
  );
}

const TONE_WORDS = {
  works: '',
  'sinks-enemy': ', sinks an enemy',
  'sinks-ally': ', sinks one of yours',
  fails: ', fails',
} as const;

/** What the hints say about a square, e.g. ", walk here" or ", shove 3 against 2". */
export function hintLabel(hints: IceHints, r: number, c: number): string {
  const parts: string[] = [];
  if (hints.moves.some((sq) => sq.r === r && sq.c === c)) parts.push('walk here');
  const badge = hints.badges.find((b) => b.target.r === r && b.target.c === c);
  if (badge) parts.push(`shove ${badge.strength} against ${badge.wall}${TONE_WORDS[badge.tone]}`);
  const route = hints.routes.find(
    (x) => x.option.action.from.r === r && x.option.action.from.c === c,
  );
  if (route) parts.push('shove from here');
  return parts.map((p) => `, ${p}`).join('');
}

/** An ARIA grid mirroring the lake: arrows move, Enter or Space activates, Escape cancels. */
function IceGrid(props: {
  state: ice.IceState;
  hints: IceHints;
  cursor: Square;
  selected: Square | null;
  facing: 'I' | 'B';
  label: string;
  text: IceBoardText;
  onCursorChange: (square: Square) => void;
  onActivate: (square: Square) => void;
  onCancel: () => void;
  onFocusChange: (focused: boolean) => void;
}) {
  const cells = useRef(new Map<string, HTMLDivElement>());
  const hasFocus = useRef(false);
  const order = screenOrder(props.facing);

  useEffect(() => {
    if (hasFocus.current) {
      cells.current.get(`${props.cursor.r}:${props.cursor.c}`)?.focus({ preventScroll: true });
    }
  }, [props.cursor]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const next = moveCursor(props.cursor, event.key, props.facing);
    if (next) {
      event.preventDefault();
      props.onCursorChange(next);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      props.onActivate(props.cursor);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      props.onCancel();
    }
  };

  return (
    <div
      role="grid"
      aria-label={props.label}
      style={hiddenGrid}
      onKeyDown={onKeyDown}
      onFocus={() => {
        hasFocus.current = true;
        props.onFocusChange(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          hasFocus.current = false;
          props.onFocusChange(false);
        }
      }}
    >
      {order.map((r) => (
        <div role="row" key={r}>
          {order.map((c) => {
            const active = props.cursor.r === r && props.cursor.c === c;
            return (
              <div
                key={c}
                role="gridcell"
                style={hiddenCell}
                tabIndex={active ? 0 : -1}
                aria-selected={props.selected?.r === r && props.selected.c === c}
                ref={(el) => {
                  if (el) cells.current.set(`${r}:${c}`, el);
                  else cells.current.delete(`${r}:${c}`);
                }}
                onFocus={() => {
                  if (!active) props.onCursorChange({ r, c });
                }}
                onClick={() => props.onActivate({ r, c })}
              >
                {props.text.squareLabel(props.state, r, c)}
                {props.text.hintLabel(props.hints, r, c)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
